import { Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import confetti from 'canvas-confetti';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useGetAllUnitsInPropertyLocation } from '@/api/endpoints/unit/useGetAllUnitsInPropertyLocation';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import {
  ChecklistItem,
  getChecklistItems,
  getNextItem,
  getNextMandatoryItem,
} from '@/utils/checklist';

import TerriLottie from '../lottie/TerriLottie';
import ModalWithPreview from '../modals/ModalWithPreview';

// Local storage key for tracking completion modal display
const COMPLETION_MODAL_SHOWN_KEY = 'onboarding_completion_modal_shown';

// Keep track of the previous onboarding progress to detect newly completed steps
const PREVIOUS_COMPLETED_STEPS_KEY = 'previous_completed_onboarding_steps';

// Helper to get only mandatory completed items (filtering out optional ones)
const getMandatoryCompletedItems = (items: ChecklistItem[]): ChecklistItem[] =>
  items.filter(item => item.isCompleted && !item.isOptional);

const OnboardingProgressModal: React.FC = () => {
  const { user } = useAuth();
  const { onboardingProgress, isOnboardingComplete, setIsMenuTourGuideOpen } =
    useOnboardingProgress();
  const { isSingleFamilyLocation, selectedPropertyLocation } = usePropertyManager();
  const initialIsOnboardingComplete = useRef(isOnboardingComplete);
  const hasShownCompletionModal = useRef(
    localStorage.getItem(COMPLETION_MODAL_SHOWN_KEY) === 'true'
  );
  const previousOnboardingComplete = useRef(isOnboardingComplete);
  const history = useHistory();

  const getAllUnits = useGetAllUnitsInPropertyLocation({
    propertyLocationId: selectedPropertyLocation?.id,
  });

  const checklistItems = useMemo(
    () =>
      getChecklistItems({
        isSingleFamilyLocation,
        onboardingProgress,
        selectedPropertyLocation,
        user,
        units: getAllUnits.data ?? [],
      }),
    [isSingleFamilyLocation, onboardingProgress, selectedPropertyLocation, user, getAllUnits.data]
  );

  const [isOpen, setIsOpen] = useState(!isOnboardingComplete && !hasShownCompletionModal.current);
  const [isAutoOpenned, setIsAutoOpenned] = useState(true);

  const theme = useTheme();

  const nextMandatoryItem = useMemo(() => getNextMandatoryItem(checklistItems), [checklistItems]);
  const nextItem = useMemo(() => getNextItem(checklistItems), [checklistItems]);

  // Find the previous completed step (not just the last item in the array)
  // Only considering mandatory (non-optional) steps
  const previousCompletedStep = useMemo(() => {
    // Get only mandatory completed items
    const mandatoryCompletedItems = getMandatoryCompletedItems(checklistItems);

    // Get previously saved completed step titles
    const prevCompletedStepsTitles = JSON.parse(
      localStorage.getItem(PREVIOUS_COMPLETED_STEPS_KEY) || '[]'
    ) as string[];

    // Get current completed step titles (only from mandatory items)
    const currentCompletedStepTitles = mandatoryCompletedItems.map(item => item.title);

    // Save current completed steps for next time
    localStorage.setItem(PREVIOUS_COMPLETED_STEPS_KEY, JSON.stringify(currentCompletedStepTitles));

    // Find newly completed steps (those in current but not in previous)
    const newlyCompletedStepTitles = currentCompletedStepTitles.filter(
      title => !prevCompletedStepsTitles.includes(title)
    );

    // If we found a newly completed step, find its item
    if (newlyCompletedStepTitles.length > 0) {
      // Prioritize building/unit completion for multi-family properties
      if (!isSingleFamilyLocation) {
        if (newlyCompletedStepTitles.includes('Add building')) {
          return mandatoryCompletedItems.find(item => item.title === 'Add building');
        }
        if (newlyCompletedStepTitles.includes('Add unit that tenants live in')) {
          return mandatoryCompletedItems.find(
            item => item.title === 'Add unit that tenants live in'
          );
        }
      }

      // Return the last newly completed step (most recent)
      const lastNewlyCompletedTitle = newlyCompletedStepTitles[newlyCompletedStepTitles.length - 1];
      return mandatoryCompletedItems.find(item => item.title === lastNewlyCompletedTitle);
    }

    // If there are no newly completed steps or storage is empty (first visit)
    // use existing logic as fallback
    if (!isSingleFamilyLocation) {
      const buildingItem = mandatoryCompletedItems.find(item => item.title === 'Add building');
      const unitItem = mandatoryCompletedItems.find(
        item => item.title === 'Add unit that tenants live in'
      );

      if (buildingItem && !unitItem) {
        return buildingItem;
      }
    }

    // Default to the last completed mandatory item in the checklist
    return mandatoryCompletedItems.length > 0
      ? mandatoryCompletedItems[mandatoryCompletedItems.length - 1]
      : undefined;
  }, [checklistItems, isSingleFamilyLocation]);

  const handleOnClose = useCallback(
    (shouldOpenMenuTourGuide = false) => {
      setIsOpen(false);
      setIsAutoOpenned(false);

      // Always set the flag when user explicitly closes the modal
      hasShownCompletionModal.current = true;
      localStorage.setItem(COMPLETION_MODAL_SHOWN_KEY, 'true');

      if (isOnboardingComplete) {
        // Navigate to the dashboard if onboarding is complete
        history.push(shouldOpenMenuTourGuide ? PMPRoutes.dashboard : PMPRoutes.dashboard);

        // Only open tour guide if explicitly requested
        if (shouldOpenMenuTourGuide) {
          setIsMenuTourGuideOpen(true);
        }
      } else {
        // Navigate to checklist page if onboarding is not complete
        history.push(PMPRoutes.onboardingChecklist);
      }
    },
    [setIsMenuTourGuideOpen, isOnboardingComplete, history]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (isAutoOpenned) return;

    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: [
        theme.palette.primary.lighter,
        theme.palette.primary.light,
        theme.palette.primary.main,
        theme.palette.primary.dark,
        theme.palette.primary.darker,
      ],
      zIndex: 9000,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAutoOpenned]);

  useEffect(() => {
    // If modal has been shown previously for completed onboarding, don't show it again
    if (hasShownCompletionModal.current) {
      setIsOpen(false);
      return;
    }

    // Track changes in completion status
    if (!previousOnboardingComplete.current && isOnboardingComplete) {
      // Onboarding just completed
      setIsOpen(true);
    } else if (previousOnboardingComplete.current && !isOnboardingComplete) {
      // Reset the shown flag when progress drops below 100%
      hasShownCompletionModal.current = false;
      localStorage.removeItem(COMPLETION_MODAL_SHOWN_KEY);
    }

    previousOnboardingComplete.current = isOnboardingComplete;

    // Initial render case - don't show if completion modal was already shown
    if (initialIsOnboardingComplete.current === false && !hasShownCompletionModal.current) {
      setIsOpen(true);
    }
  }, [onboardingProgress, isOnboardingComplete]);

  if (!isOpen) return null;

  return (
    <ModalWithPreview
      fullScreen={false}
      maxWidth="md"
      title={isOnboardingComplete ? 'Onboarding complete!' : "Let's finish your onboarding"}
      onClose={() => handleOnClose(false)}
      Actions={
        <>
          <Button variant="outlined" color="secondary" onClick={() => handleOnClose(false)}>
            Close
          </Button>

          {!!nextMandatoryItem && (
            <Button
              variant="contained"
              color="primary"
              href={nextMandatoryItem.route}
              onClick={() => handleOnClose(false)}
            >
              {nextMandatoryItem.actionLabel}
            </Button>
          )}

          {isOnboardingComplete && (
            <Button
              variant="contained"
              color="primary"
              href={PMPRoutes.dashboard}
              onClick={() => handleOnClose(true)}
            >
              Take Guided Tour
            </Button>
          )}
        </>
      }
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box width={128} height={128}>
          <TerriLottie size={128} />
        </Box>

        <Stack direction="column" spacing={1}>
          {!!previousCompletedStep && !isAutoOpenned && !isOnboardingComplete && (
            <>
              <Typography variant="body2">
                You've <strong>{previousCompletedStep?.completedTitle}</strong>, good job!
              </Typography>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {nextMandatoryItem ? (
            <>
              <Typography variant="body2">
                {isAutoOpenned ? "Let's" : "Now, let's"}{' '}
                <strong>{nextMandatoryItem.letsTitle}</strong>.
              </Typography>

              {!!nextMandatoryItem.benefits?.length && (
                <Typography variant="body2">
                  {nextMandatoryItem.letsDescription ?? 'This'} will allow you to:
                  <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                    {nextMandatoryItem.benefits?.map(benefit => (
                      <li key={benefit}>{benefit}</li>
                    ))}
                  </Box>
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2">
                You've completed <strong>all setup steps</strong>!
              </Typography>

              <Typography variant="body2">Your property management system is now ready.</Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2">
                I'll be in touch if I need anything, but in the meantime, feel free to upload any
                additional documents, invoices, or warranties for your property. The more I know,
                the better I can support you and proactively assist with your tenant's questions!
              </Typography>

              <Typography variant="body2">
                I'll also keep you in the loop with real-time alerts whenever a tenant reaches out,
                so you're always up to date!
              </Typography>
            </>
          )}

          {!!nextItem?.isOptional && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography variant="body2">
                You have an optional step to complete: <strong>{nextItem.letsDescription}</strong>.
              </Typography>

              {!!nextItem.benefits?.length && (
                <Typography variant="body2">
                  This will allow you to:
                  <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                    {nextItem.benefits?.map(benefit => (
                      <li key={benefit}>{benefit}</li>
                    ))}
                  </Box>
                </Typography>
              )}

              <Box>
                <Button
                  variant="text"
                  color="primary"
                  href={nextItem.route}
                  onClick={() => handleOnClose(false)}
                >
                  {nextItem.actionLabel}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Stack>
    </ModalWithPreview>
  );
};

export default OnboardingProgressModal;
